import React, { useContext } from "react";
import { usePageIntro } from "common/NewLook";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH, SMALL_SCREEN } from "common/constants.ts";
import ApiSlotsSelector from "features/settings/Subscription/ApiSlotsSelector";
import Summary from "features/settings/Billing/ApiSlots/Summary.tsx";
import {
  FormInnerWrapper,
  FormWithSummaryWrapper,
  FormWrapper,
  SummaryInnerWrapper,
  SummaryWrapper,
} from "features/settings/common/styles.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import styled, { useTheme } from "styled-components";
import { BOTTOM_SPACER_HEIGHT } from "common/Home/General/constants.tsx";
import {
  useDisablePageScroll,
  useHideBottomSpacer,
  useBottomNavigator,
} from "utils/hooks/misc.tsx";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import ApiSlotsPriceRowComponent from "features/settings/Subscription/ApiSlotsPriceRowComponent";
import { Divider } from "common/styles.ts";
import { SubscriptionPaymentMethod } from "@shared/features/settings/subscription/types.ts";
import SummaryRow from "features/settings/Subscription/SummaryRow";
import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import { toFixed } from "@shared/utils/numbers.ts";
import { getApiSlotsValidation } from "features/settings/common/apiSlotsValidation.ts";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import BuySubscriptionProvider from "features/settings/Subscription/BuySubscriptionProvider.tsx";
import NewLookCheckoutLayout, {
  NewLookMobileCheckout,
} from "features/settings/Subscription/NewLook/NewLookCheckoutLayout.tsx";
import { useHistory } from "react-router-dom";
import { BASE_BILLING_SETTINGS_PATH } from "common/constants.ts";

const ApiSlotsWithSummary = () => (
  <BuySubscriptionProvider isStandaloneApiSlots={true}>
    <ApiSlotsWithSummaryInner />
  </BuySubscriptionProvider>
);

export default ApiSlotsWithSummary;

const ApiSlotsWithSummaryInner = () => {
  const { isNewLook } = useTheme();
  // Focused flow with its own title.
  usePageIntro({ hidden: isNewLook }, [isNewLook]);
  const { isFetchingPaymentMethods } = useContext(BuySubscriptionContext)!;
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const isBottomSummary = useMedia([`(max-width: 900px)`], [true], false);
  // The new-look phone layout scrolls as one column.
  useDisablePageScroll(isBottomSummary && !isNewLook);
  useHideBottomSpacer();
  const history = useHistory();
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);

  const form = (
    <>
      <ApiSlotsSelector isBottomSummary={isBottomSummary} />
      <ApiSlotsPricingSection />
    </>
  );

  if (isBottomSummary && isNewLook) {
    return (
      <LoadingSpinnerOverlay
        isLoading={isFetchingPaymentMethods}
        hideContentWhileLoading={isFetchingPaymentMethods}
        spinnerSize="large"
      >
        <NewLookMobileCheckout
          title={t("api_slots.title")}
          form={form}
          summary={<Summary />}
        />
      </LoadingSpinnerOverlay>
    );
  }

  if (isBottomSummary) {
    return (
      <LoadingSpinnerOverlay
        isLoading={isFetchingPaymentMethods}
        hideContentWhileLoading={isFetchingPaymentMethods}
        spinnerSize="large"
      >
        <FormWithSummaryWrapper
          $isMobile={true}
          style={{
            paddingTop: 0,
            flex: 1,
            minHeight: 0,
          }}
        >
          <MobileSummaryWrapper>
            <Summary />
          </MobileSummaryWrapper>
          <MobileFormWrapper>{form}</MobileFormWrapper>
        </FormWithSummaryWrapper>
      </LoadingSpinnerOverlay>
    );
  }

  if (isNewLook) {
    return (
      <NewLookCheckoutLayout
        title={t("api_slots.title")}
        onBack={() => history.push(BASE_BILLING_SETTINGS_PATH)}
        backLabel={t("cancel_subscription.back_to_billing")}
        form={form}
        summary={<Summary />}
      />
    );
  }

  return (
    <FormWithSummaryWrapper style={{ flex: 1 }}>
      <FormWrapper $isSmallScreen={isSmallScreen}>
        <FormInnerWrapper>{form}</FormInnerWrapper>
      </FormWrapper>
      <SummaryWrapper $isSmallScreen={isSmallScreen}>
        <SummaryInnerWrapper $isSmallScreen={isSmallScreen}>
          <Summary />
        </SummaryInnerWrapper>
      </SummaryWrapper>
    </FormWithSummaryWrapper>
  );
};

const ApiSlotsPricingSection = () => {
  const {
    isUpgradePricesLoading,
    subscriptionPaymentMethod,
    cryptoTotalPrice,
    paymentMethod,
    upgradePrices,
    numApiSlots,
  } = useContext(BuySubscriptionContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const isCryptoPayment =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.CRYPTO;
  const pricePerYear = upgradePrices?.slots_setup_price ?? 0;
  const extraFee = isCryptoPayment ? 0.05 * pricePerYear : 0;
  const totalCryptoData = {
    show: isCryptoPayment && cryptoTotalPrice !== undefined,
    title: `${paymentMethod?.payment_option} ${t("plan_summary.to_pay")}`,
    price: parseFloat(cryptoTotalPrice || "0"),
  };
  const { isInvalidStep } = getApiSlotsValidation(numApiSlots ?? 0);

  return (
    <LoadingSpinnerOverlay isLoading={isUpgradePricesLoading} wrap={true}>
      <ApiSlotsPriceRowComponent />
      {isCryptoPayment && (
        <>
          <SummaryRow
            label={t("api_slots.crypto_fee")}
            price={extraFee}
            style={{ marginTop: 12 }}
          />
          <Divider />
          <Flex>
            <Title level={isBottomNavigator ? 4 : 3}>
              {t("api_slots.total")}
            </Title>
            {isInvalidStep ? (
              <Title level={isBottomNavigator ? 4 : 3}>N/A</Title>
            ) : (
              <Title level={isBottomNavigator ? 4 : 3}>
                ${toFixed(pricePerYear + extraFee, 2)}
              </Title>
            )}
          </Flex>
          {totalCryptoData.show && (
            <Flex style={{ marginTop: 8 }}>
              <Title level={isBottomNavigator ? 4 : 3}>
                {totalCryptoData.title}
              </Title>
              <Flex>
                <TotalPrice as="div">
                  <TextWithIcon
                    text={totalCryptoData.price}
                    iconLink={paymentMethod?.icon_url}
                    textStyle={{
                      fontSize:
                        typography.fontSizes[isBottomNavigator ? "M" : "L"],
                    }}
                  />
                </TotalPrice>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </LoadingSpinnerOverlay>
  );
};

const TotalPrice = styled.div`
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;

const MobileSummaryWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;
const MobileFormWrapper = styled.div`
  border-top: ${(p) => p.theme.borders.light};
  padding: 16px 16px ${BOTTOM_SPACER_HEIGHT}px;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 12px;
  }
`;
